<template>
  <div class="fts-settings-page" @input.capture="touched = true" @change.capture="touched = true">
    <header class="fts-settings-head">
      <div>
        <h1>Profile</h1>
        <p>How you appear to the people you work with.</p>
      </div>
    </header>

    <section class="fts-settings-card">
      <div class="fts-settings-profile">
        <div class="fts-settings-photo">
          <img :src="photo" alt="" />
          <div class="fts-settings-photo-actions">
            <button
              type="button"
              class="fts-settings-btn"
              id="fts-settings-pick-photo"
              :disabled="uploading"
            >
              {{ uploading ? "Uploading…" : (formData.profilePictureUrl ? "Change photo" : "Add a photo") }}
            </button>
            <button
              type="button"
              class="fts-settings-btn"
              v-if="formData.profilePictureUrl"
              :disabled="uploading"
              @click="removePhoto"
            >
              Remove
            </button>
          </div>
        </div>

        <div class="fts-settings-grid-2 fts-settings-profile-fields">
          <fts-form-element
            v-for="field in fields"
            :key="field.fieldName"
            :item="field"
            :form-data="formData"
            :validator="$v.formData"
            comfort-design
          />
        </div>
      </div>

      <avatar-cropper
        trigger="#fts-settings-pick-photo"
        :upload-url="uploadUrl"
        :upload-form-data="{ profile: true }"
        :labels="{ submit: 'Done', cancel: 'Cancel' }"
        :output-options="{ width: 512, height: 512 }"
        @uploading="uploading = true"
        @completed="uploading = false"
        @error="onUploadError"
        @uploaded="onUploaded"
      />
    </section>

    <!-- On behalf. NotificationRecipientManager redirects every channel -- email, Slack, in-app,
         SMS, WhatsApp -- to the named person, and only while all three fields are set and today
         falls between the dates. It moves NOTIFICATIONS only: nothing assigned to you is
         reassigned. The old tab called the person "On Behalf Of", which reads the wrong way
         round -- they are the one who receives. -->
    <section class="fts-settings-card">
      <div>
        <h2>While you are away</h2>
        <p class="fts-settings-card-sub">
          Between two dates, your notifications go to someone else instead, on every channel. Only
          notifications move: anything assigned to you stays yours.
        </p>
      </div>

      <div class="fts-settings-row">
        <div class="fts-settings-row-text">
          <strong>Send my notifications to someone else</strong>
          <span>{{ onBehalfSummary }}</span>
        </div>
        <fts-toggle-switch v-model="onBehalf" />
      </div>

      <div class="fts-settings-grid-2" v-if="formData.onBehalf">
        <fts-form-element
          :item="{ fieldName: 'startDate', type: 5 }"
          :form-data="formData"
          comfort-design
        />
        <fts-form-element
          :item="{ fieldName: 'endDate', type: 5 }"
          :form-data="formData"
          comfort-design
        />
        <fts-form-element
          :item="{ displayName: 'Send them to (username)', fieldName: 'onBehalfUsername' }"
          :form-data="formData"
          comfort-design
        />
      </div>
    </section>

    <div class="fts-settings-save" v-if="dirty">
      <div class="fts-settings-save-actions">
        <button type="button" class="fts-settings-btn" :disabled="saving" @click="discard">
          Discard
        </button>
        <button type="button" class="fts-settings-btn is-primary" :disabled="saving" @click="save">
          {{ saving ? "Saving…" : "Save changes" }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Settings › Your account › Profile — the old profile modal's Profile tab, moved into the frame.
 *
 * It saves through the same `updateUserProfile` action, with the same ten fields and nothing
 * else: the action posts the whole payload to `api/user/update`, and anything extra riding along
 * would be the server's to guess at. The profile itself is read from the store the login already
 * filled, so opening the page costs no request.
 *
 * The page is the PERSON's, not the workspace's. The frame says so above it (`scope: "account"`),
 * and nothing here reads the workspace at all.
 */
import AvatarCropper from "vue-avatar-cropper";
import commons from "../../commons/commons";
import webAppConfig from "../../commons/webApp.config";
import { authorizationStore } from "../../store/authorizationStore";
import FTSFormElement from "../../elements/FTSFormElement";
import FTSToggleSwitch from "../../elements/FTSToggleSwitch";

/** Exactly what the old tab posted. The endpoint takes the whole object. */
const SAVED_FIELDS = [
  "displayName", "firstName", "lastName", "email", "phoneNumber", "profilePictureUrl",
  "onBehalf", "startDate", "endDate", "onBehalfUsername",
];

/** Long enough for a field component to finish reformatting what it was given. */
const SETTLE_MS = 600;

function parseDay(value) {
  if (!value) return null;
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
}

function dayLabel(d) {
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

function same(a, b) {
  const left = a === null || a === undefined ? "" : a;
  const right = b === null || b === undefined ? "" : b;
  return String(left) === String(right);
}

export default {
  name: "SettingsProfile",
  components: {
    AvatarCropper,
    "fts-form-element": FTSFormElement,
    "fts-toggle-switch": FTSToggleSwitch,
  },
  props: {
    workspaceId: [String, Number],
  },
  data() {
    return {
      formData: {},
      originalData: {},
      touched: false,
      saving: false,
      uploading: false,
    };
  },
  validations() {
    return { formData: commons.getFormValidators(this.fields) };
  },
  computed: {
    fields() {
      return [
        { type: 0, fieldName: "userName", readOnly: true },
        { type: 0, fieldName: "displayName", required: true },
        { type: 0, fieldName: "firstName" },
        { type: 0, fieldName: "lastName" },
        { type: 26, fieldName: "phoneNumber" },
        { type: 25, fieldName: "email" },
      ];
    },
    uploadUrl() {
      return webAppConfig.fileUploadURL;
    },
    /** The old tab's rule: an uploaded path is relative to the API, which may be another origin. */
    photo() {
      const url = this.formData.profilePictureUrl;
      if (!url) return commons.defaultProfileImage;
      return webAppConfig.baseURL && url.startsWith("/") ? webAppConfig.baseURL + url : url;
    },
    /**
     * What the server will actually do, in its own terms: redirect only while start <= now <=
     * end + 1 day AND a person is named. A switch that is on with a date missing does nothing at
     * all, and saying "On" for it would be the page's word against the code's.
     */
    onBehalfSummary() {
      const f = this.formData;
      if (!f.onBehalf) return "Off — notifications come to you";
      const start = parseDay(f.startDate);
      const end = parseDay(f.endDate);
      const who = f.onBehalfUsername;
      if (!start || !end || !who) return "Not in effect until it has a start date, an end date and a person";
      const now = new Date();
      const endOfWindow = new Date(end.getTime() + 24 * 60 * 60 * 1000);
      if (now < start) return "From " + dayLabel(start) + ", your notifications will go to " + who;
      if (now > endOfWindow) return "Ended " + dayLabel(end) + " — notifications come to you again";
      return "In effect — your notifications are going to " + who + " until " + dayLabel(end);
    },
    dirty() {
      return SAVED_FIELDS.some(key => !same(this.originalData[key], this.formData[key]));
    },
    /** Through $set, because a profile that never had on-behalf has no such key to react to. */
    onBehalf: {
      get() {
        return !!this.formData.onBehalf;
      },
      set(value) {
        this.touched = true;
        this.$set(this.formData, "onBehalf", !!value);
      },
    },
  },
  methods: {
    load() {
      const profile = authorizationStore.getters.userProfile || {};
      this.formData = JSON.parse(JSON.stringify(profile));
      this.originalData = JSON.parse(JSON.stringify(profile));
      // Field components reformat what they are given (the phone field most of all), so the
      // baseline is taken again once they settle -- unless someone has already started typing.
      this.touched = false;
      window.setTimeout(() => {
        if (!this.touched) this.originalData = JSON.parse(JSON.stringify(this.formData));
      }, SETTLE_MS);
    },
    discard() {
      this.formData = JSON.parse(JSON.stringify(this.originalData));
    },
    removePhoto() {
      this.$set(this.formData, "profilePictureUrl", null);
    },
    onUploaded(res) {
      this.uploading = false;
      if (res && res.url) this.$set(this.formData, "profilePictureUrl", res.url);
    },
    onUploadError(message) {
      this.uploading = false;
      this.$toasted.global.error({}, message || "That photo could not be uploaded.");
    },
    async save() {
      if (this.saving) return;
      this.$v.formData.$touch();
      if (this.$v.formData.$invalid) {
        this.$toasted.global.invalidFormValidate();
        return;
      }
      this.saving = true;
      try {
        const payload = {};
        SAVED_FIELDS.forEach(key => { payload[key] = this.formData[key]; });
        await authorizationStore.dispatch("updateUserProfile", payload);
        this.originalData = JSON.parse(JSON.stringify(this.formData));
        this.$toasted.success("Profile updated successfully.");
      } catch (err) {
        this.$toasted.global.error(err, "Your profile could not be saved.");
      } finally {
        this.saving = false;
      }
    },
  },
  mounted() {
    this.load();
  },
};
</script>
